import {Button, Form, Modal, Spinner} from 'react-bootstrap'
import {IMembersNode, IResAddAssignmentGroup} from '../../../types/assignmentGroups'
import {useLazyQuery, useMutation, useQuery} from '@apollo/client'
import {GET_ALL_USERS_LIST} from '../../../gql/queries/usersQuery'
import {useEffect, useMemo, useState} from 'react'
import {toast} from 'react-toastify'
import {IResAllListUsers} from '../../../types/users'
import {parseId} from '../../../helpers'
import {GET_ALL_ORGANIZATIONS} from '../../../gql/queries/organizationsQuery'
import {useGetUser} from '../../../store/userStore'
import {MultiSelectWithActions} from '../../../components/MultiSelectWithActions'

import * as Yup from 'yup'
import {useFormik} from 'formik'
import {CREATE_GROUP_ASSIGNMENT, UPDATE_GROUP_ASSIGNMENT} from '../../../../modules/assignmentGroupMutation'

interface IModalGroups {
  isOpen: boolean
  onClose: () => void
  refetch: () => void
  infoGroup?: null | {
    id: string
    name: string
    members: IMembersNode[]
    supervisorIds?: (string | number)[]
    organizationId?: string | number
    visibleForAgents?: boolean
  }
}

const validationSchema = Yup.object().shape({
  name: Yup.string().required('El nombre es requerido'),
  supervisors: Yup.array().of(Yup.string()),
})

export const ModalAddAssignmentGroup: React.FC<IModalGroups> = ({
  isOpen,
  onClose,
  infoGroup,
  refetch,
}) => {
  const [search, setSearch] = useState('')
  const normalizeId = (id?: string | number | null) => (id ? `${parseId(id)}` : '')

  // Get current user's role and organization
  const userGroups = useGetUser((s) => s.user?.groups) || []
  const currentUser = useGetUser((s) => s.user?.user)
  const isSuperAdministrador = userGroups.includes('SuperAdministrador')

  const mutationDoc = infoGroup ? UPDATE_GROUP_ASSIGNMENT : CREATE_GROUP_ASSIGNMENT
  const [gqlCreate, {data: dataCreate, loading: loadingCreate, error: errorCreate}] =
    useMutation<IResAddAssignmentGroup>(mutationDoc)

  const [gqlGetFilterMembers, {data, loading, error}] = useLazyQuery<IResAllListUsers>(
    GET_ALL_USERS_LIST,
    {fetchPolicy: 'no-cache'}
  )

  // Query for organizations
  const {
    data: dataOrganizations,
    loading: loadingOrganizations,
    error: errorOrganizations,
  } = useQuery(GET_ALL_ORGANIZATIONS, {
    variables: {first: 100},
    fetchPolicy: 'no-cache',
  })

  // Filtrar supervisores del listado de usuarios
  const supervisors = useMemo(() => {
    if (!data?.allUsersList) return []
    return data.allUsersList.filter((user) => user.primaryGroup?.name === 'Supervisor')
  }, [data])

  const supervisorOptions = useMemo(
    () =>
      supervisors.map((supervisor) => ({
        value: String(parseId(supervisor.id)),
        label: `${supervisor.firstName} ${supervisor.lastName} - ${supervisor.email}`,
      })),
    [supervisors]
  )

  // Función helper para filtrar usuarios por búsqueda
  const filterUsersBySearch = useMemo(() => {
    return (searchText: string) => {
      if (!data?.allUsersList) return []

      const searchLower = searchText.toLowerCase()

      return data.allUsersList.filter((node) => {
        const fullName = `${node.firstName} ${node.lastName}`.toLowerCase()

        // Buscar en nombre, email o grupos
        const matchesNameOrEmail =
          fullName.includes(searchLower) ||
          node.email.toLowerCase().includes(searchLower)

        // Buscar en los nombres de los grupos
        const matchesGroup = node.groupAssignment?.edges.some(({node: group}) =>
          group.name.toLowerCase().includes(searchLower)
        ) || false

        return matchesNameOrEmail || matchesGroup
      })
    }
  }, [data])

  // Usuarios filtrados según la búsqueda
  const filteredUsers = useMemo(() => {
    return filterUsersBySearch(search)
  }, [search, filterUsersBySearch])

  const formik = useFormik({
    initialValues: {
      name: '',
      user: [] as (string | number)[],
      supervisors: [] as (string | number)[],
      organizationId: '' as string | number,
      visibleForAgents: false,
    },
    validationSchema: validationSchema,
    onSubmit: (values) => {
      const payload = {
        name: values.name,
        userIds: values.user,
        supervisorIds: values.supervisors.map(id => parseId(id)),
        organizationId: values.organizationId ? parseId(values.organizationId) : undefined,
        visibleForAgents: values.visibleForAgents,
      }

      if (infoGroup) {
        gqlCreate({
          variables: {
            groupId: parseId(infoGroup.id),
            groupData: payload,
          },
        })
      } else {
        gqlCreate({
          variables: {
            groupData: payload,
          },
        })
      }
    },
  })

  const selectedSupervisorOptions = useMemo(() => {
    const selectedIds = (formik.values.supervisors || []).map((id) => String(parseId(id)))
    return supervisorOptions.filter((opt) => selectedIds.includes(String(opt.value)))
  }, [formik.values.supervisors, supervisorOptions])

  useEffect(() => {
    gqlGetFilterMembers()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!infoGroup) {
      // Set organization from current user if not SuperAdministrador
      if (!isSuperAdministrador && currentUser?.organization?.id) {
        formik.setFieldValue('organizationId', normalizeId(currentUser.organization.id))
      }
      return
    }
    formik.setValues({
      name: infoGroup.name,
      user: infoGroup.members.map(({node}) => parseId(node.id)),
      supervisors: infoGroup.supervisorIds?.map(id => parseId(id)) || [],
      organizationId: infoGroup.organizationId || (currentUser?.organization?.id ? normalizeId(currentUser.organization.id) : ''),
      visibleForAgents: Boolean(infoGroup.visibleForAgents),
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [infoGroup])

  useEffect(() => {
    if (error) toast.error('Error: ' + error.message)
  }, [error])

  useEffect(() => {
    if (errorOrganizations) toast.error('Error loading organizations: ' + errorOrganizations.message)
  }, [errorOrganizations])

  useEffect(() => {
    if (!dataCreate) return
    const payload = dataCreate.createGroupAssignmentMutation || dataCreate.updateGroupAssignmentMutation
    if (payload?.groupAssignment?.id) {
      toast.success(infoGroup ? 'Assignment group updated successfully' : 'Assignment group created successfully')
      refetch()
      onClose()
    } else if (payload?.errors?.length) {
      toast.error(
        `Error ${
          payload.errors[0]?.message || 'Something happened'
        }`
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataCreate])

  useEffect(() => {
    if (errorCreate) toast.error('Error: ' + errorCreate.message)
  }, [errorCreate])

  return (
    <Modal show={isOpen} onHide={onClose}>
      <Modal.Header closeButton>
        <Modal.Title>{infoGroup ? `Edit ${infoGroup.name}` : 'Add Assignment Group'}</Modal.Title>
      </Modal.Header>
      {loading || !data ? (
        <>
          <Modal.Body>
            <Form.Group className='mb-3' controlId='name'>
              <Form.Label>Name</Form.Label>
              <Form.Control placeholder='Enter Group name' disabled />
            </Form.Group>
            <Form.Group className='mb-3' controlId='supervisors'>
              <Form.Label>Supervisors</Form.Label>
              <div style={{padding: '10px', border: '1px solid #dbdfe9', borderRadius: '4px', backgroundColor: '#f5f5f5'}}>
                <Spinner animation='border' size='sm' /> Loading supervisors...
              </div>
            </Form.Group>
            <Form.Group className='mb-3' controlId='organizationId'>
              <Form.Label>Organization</Form.Label>
              <Form.Select disabled>
                <option value=''>Loading...</option>
              </Form.Select>
            </Form.Group>
            <div className='mt-8'>
              <h6 className='mb-4'>Members</h6>
              <div
                className='w-100 d-flex align-items-center justify-content-center'
                style={{height: '70px'}}
              >
                <Spinner animation='border' />
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button disabled variant='secondary'>
              Close
            </Button>
            <Button disabled variant='primary'>
              Save Changes
            </Button>
          </Modal.Footer>
        </>
      ) : (
        <Form onSubmit={formik.handleSubmit}>
          <Modal.Body>
            <Form.Group className='mb-3' controlId='name'>
              <Form.Label>Name</Form.Label>
              <Form.Control
                placeholder='Enter Group name'
                value={formik.values.name}
                onChange={formik.handleChange}
                isInvalid={Boolean(formik.touched.name && formik.errors.name)}
              />
              <Form.Control.Feedback type='invalid'>{formik.errors.name}</Form.Control.Feedback>
            </Form.Group>

            <Form.Group className='mb-3' controlId='supervisors'>
              <Form.Label>Supervisors (optional)</Form.Label>
              <MultiSelectWithActions
                value={selectedSupervisorOptions}
                options={supervisorOptions}
                onChange={(selected) => {
                  const values = Array.isArray(selected)
                    ? selected.map((opt: any) => String(opt?.value || '').trim()).filter(Boolean)
                    : []
                  formik.setFieldValue('supervisors', values)
                }}
              />
              {supervisors.length === 0 && (
                <Form.Text className='text-muted'>
                  No supervisors available. Only users with "Supervisor" role will appear here.
                </Form.Text>
              )}
            </Form.Group>

            <Form.Group className='mb-3' controlId='organizationId'>
              <Form.Label>Organization</Form.Label>
              <Form.Select
                name='organizationId'
                value={formik.values.organizationId}
                onChange={formik.handleChange}
                isInvalid={Boolean(formik.errors.organizationId && formik.touched.organizationId)}
                disabled={!isSuperAdministrador || loadingOrganizations}
              >
                {loadingOrganizations ? (
                  <option value=''>Loading...</option>
                ) : (
                  <option value=''>Select One</option>
                )}
                {dataOrganizations?.allOrganizations?.edges?.map(({node}: any) => {
                  const value = normalizeId(node.id)
                  return (
                    <option key={node.id} value={value}>
                      {node.name}
                    </option>
                  )
                })}
              </Form.Select>
              <Form.Control.Feedback type='invalid'>
                {formik.errors.organizationId}
              </Form.Control.Feedback>
              {!isSuperAdministrador && (
                <Form.Text className='text-muted'>
                  Only SuperAdministrador can change the organization
                </Form.Text>
              )}
            </Form.Group>

            <Form.Group className='mb-3' controlId='visibleForAgents'>
              <Form.Check
                type='checkbox'
                name='visibleForAgents'
                label='Visible for agents (ticket fallback)'
                checked={Boolean(formik.values.visibleForAgents)}
                onChange={formik.handleChange}
              />
              <Form.Text className='text-muted'>
                If enabled, agents can see this group in ticket assignment even if they are not members.
              </Form.Text>
            </Form.Group>

            <Form.Control
              type="text"
              placeholder="Search by name or email..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="mb-3"
            />
            <div
              className='mt-8'
              style={{
                overflowY: 'auto',
                maxHeight: '350px',
              }}
            >
              <h6 className='mb-4'>Members</h6>
              <Form.Check
                inline
                label="Select All"
                name="selectAll"
                type="checkbox"
                id="select-all"
                checked={filteredUsers.every((node) => formik.values.user.includes(parseId(node.id))) && filteredUsers.length > 0}
                onChange={(e) => {
                  const isChecked = e.target.checked
                  const filteredUserIds = filteredUsers.map((node) => parseId(node.id))
                  if (isChecked) {
                    const newUsers = Array.from(new Set([...formik.values.user, ...filteredUserIds]))
                    formik.setFieldValue('user', newUsers)
                  } else {
                    const newUsers = formik.values.user.filter((userId) => !filteredUserIds.includes(userId))
                    formik.setFieldValue('user', newUsers)
                  }
                }}
                className="mb-3"
              />
              {filteredUsers.map((node) => {
                const userGroups = node.groupAssignment?.edges.map(({node: group}) => group.name) || []

                return (
                  <div key={node.id} className='mb-3'>
                    <Form.Check
                      inline
                      label={
                        <div className='d-flex flex-column p-0'>
                          <p className='p-0 m-0'>
                            {node.firstName} {node.lastName} - {node.email}
                          </p>
                          <small className='text-muted'>{node.rol}</small>
                          {userGroups.length > 0 && (
                            <small className='text-info'>
                              <i className='bi bi-people-fill me-1'></i>
                              {userGroups.join(', ')}
                            </small>
                          )}
                        </div>
                      }
                      name='group1'
                      checked={formik.values.user.includes(parseId(node.id))}
                      id={`check-${parseId(node.id)}`}
                      onChange={(e) => {
                        const isChecked = e.target.checked
                        if (isChecked) {
                          formik.setFieldValue('user', [...formik.values.user, parseId(node.id)])
                        } else {
                          const updatedMembers = formik.values.user.filter(
                            (m) => m !== parseId(node.id)
                          )
                          formik.setFieldValue('user', updatedMembers)
                        }
                      }}
                    />
                  </div>
                )
              })}
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button type='button' variant='secondary' onClick={onClose}>
              Close
            </Button>
            <Button
              type='submit'
              variant='primary'
              disabled={loadingCreate}
              className='d-flex align-items-center gap-2'
            >
              {loadingCreate && <Spinner size='sm' animation='border' />}
              Save Changes
            </Button>
          </Modal.Footer>
        </Form>
      )}
    </Modal>
  )
}
